import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import { Save, Loader2, TrendingUp, Users, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    customers: '30+',
    projects_done: '38',
    results_guaranteed: '100'
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setFormData({
        customers: response.data.customers || '30+',
        projects_done: response.data.projects_done || '38',
        results_guaranteed: response.data.results_guaranteed || '100'
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/stats`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Saved!', description: 'Stats updated successfully.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save stats.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#ff6b35]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Website Stats</h1>
            <p className="text-gray-500 mt-1">Update the statistics shown on your website</p>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] hover:from-[#e55a2b] hover:to-[#ff6b35]"
            data-testid="save-stats-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-2xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Happy Clients</label>
              <Input
                value={formData.customers}
                onChange={(e) => setFormData({ ...formData, customers: e.target.value })}
                placeholder="30+"
                className="text-center text-2xl font-bold rounded-xl"
              />
              <p className="text-xs text-gray-400 mt-2">e.g., "30+" or "50"</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-2xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center">
                <Briefcase className="w-8 h-8 text-white" />
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Projects Delivered</label>
              <Input
                value={formData.projects_done}
                onChange={(e) => setFormData({ ...formData, projects_done: e.target.value })}
                placeholder="38"
                className="text-center text-2xl font-bold rounded-xl"
              />
              <p className="text-xs text-gray-400 mt-2">e.g., "38" or "50+"</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-2xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Success Rate (%)</label>
              <Input
                value={formData.results_guaranteed}
                onChange={(e) => setFormData({ ...formData, results_guaranteed: e.target.value })}
                placeholder="100"
                className="text-center text-2xl font-bold rounded-xl"
              />
              <p className="text-xs text-gray-400 mt-2">Shown as percentage</p>
            </div>
          </div>

          <div className="mt-8 p-4 bg-orange-50 rounded-xl border border-orange-100">
            <p className="text-sm text-orange-800">
              <strong>Preview:</strong> These stats will appear on the homepage hero section as "{formData.projects_done}+ Projects", "{formData.customers} Clients", and "{formData.results_guaranteed}% Success Rate".
            </p>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default StatsPage;
